import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolDefinition, ToolExecutionContext } from '../types';
import { COLOR_FILTER_GLOBAL, COLOR_FILTER_NONE } from '../video-state/constants';
import { isNonRecommendable } from '../video-state/serialization/assetSerializer';
import type { MediaEffectName } from '../video-state/serialization/mediaHelpers';
import {
    getMediaColorGradingOverride,
    MEDIA_EFFECT_PROPERTIES,
} from '../video-state/serialization/mediaHelpers';

import {
    activeColorGradingFilters,
    colorGradingFilterParameter,
    findColorGradingFilterByName,
    loadActiveColorGradingFilters,
    unknownColorGradingFilterError,
} from './color-grading-filters';
import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import type { ColorGradingFilter } from 'js/src/client';
import { MONO_FILTER_TEXTURE_URL } from 'js/src/data/color-grading/color-grading-slice';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import type { MediaEffectValues } from 'js/src/luminary/luminary-operations';
import {
    DEFAULT_MEDIA_EFFECTS,
    getMediaEffectValues,
    removeColorGradingFilterOverrideOperation,
    setColorGradingFilterOverrideIntensityOperation,
    setColorGradingFilterOverrideOperation,
    setSceneMediaEffectsOperation,
} from 'js/src/luminary/luminary-operations';
import { getAdvancedMediaControlsAsset } from 'js/src/luminary/luminary-selectors';

// color_filter's enum and description come from the filters injected per agent instance, so they
// are added in extendParameters rather than here.
export const setMediaEffectsSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The media asset name (e.g. media_1)'),
    color_filter: z.string().optional(),
    color_filter_intensity: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(
            'Colour filter intensity from 0.0 (no effect) to 1.0 (full effect). Defaults to 1 with color_filter; on its own it changes the intensity of the filter this asset already shows.',
        ),
    contrast: z
        .number()
        .min(0)
        .max(5)
        .optional()
        .describe('Contrast multiplier: 1 = unchanged, below 1 flatter, above 1 punchier (max 5).'),
    hue: z
        .number()
        .min(-180)
        .max(180)
        .optional()
        .describe('Hue rotation in degrees: 0 = unchanged, ±180 = opposite colours.'),
    saturation: z
        .number()
        .min(-1)
        .max(1)
        .optional()
        .describe(
            'Saturation shift: 0 = unchanged, -1 = fully desaturated (grey), 1 = maximally vivid.',
        ),
    lightness: z
        .number()
        .min(-1)
        .max(1)
        .optional()
        .describe('Lightness shift: 0 = unchanged, -1 = much darker, 1 = much lighter.'),
    blur: z
        .number()
        .min(0)
        .max(10)
        .optional()
        .describe('Blur strength: 0 = sharp, 10 = heavily blurred (useful behind text).'),
    vignette: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe('Vignette (darkened edges): 0 = none, 1 = strongest.'),
    noise: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe('Film grain: 0 = none, 1 = strongest. Ignored on stickers.'),
    reset: z
        .boolean()
        .optional()
        .describe(
            "Set to true to restore contrast, hue, saturation, lightness, blur, vignette and noise to their defaults and make the asset follow the video's global colour grading again (dropping any filter of its own). Any other value in the same call is applied on top of the reset. Brightness and playback are left alone.",
        ),
});
export type SetMediaEffectsArgs = z.infer<typeof setMediaEffectsSchema>;

// Every parameter other than the asset address is an adjustment; a call must make at least one.
const ADJUSTMENT_PARAMETERS = (
    Object.keys(setMediaEffectsSchema.shape) as (keyof SetMediaEffectsArgs)[]
).filter((parameter) => parameter !== 'scene_id' && parameter !== 'asset_name');

type ColorFilterChange =
    | { kind: 'follow_global' }
    | { kind: 'ungraded' }
    | { kind: 'intensity'; intensity: number; filterName: string }
    | { kind: 'filter'; filter: ColorGradingFilter; intensity: number; isGreyscale: boolean };

const formatPercent = (fraction: number): string => `${Math.round(fraction * 100)}% intensity`;

interface SetMediaEffectsConstructorArgs extends ToolConstructorArgs {
    colorGradingFilters?: ColorGradingFilter[];
}

export class SetMediaEffectsTool extends VideoStateTool<
    typeof setMediaEffectsSchema,
    SetMediaEffectsConstructorArgs
> {
    protected override readonly schema = setMediaEffectsSchema;
    protected override readonly toolName = TOOL_NAMES.setMediaEffects;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Adjust how one media asset looks: its colour grading (a filter applied to this asset only, overriding the video's global filter), colour correction (contrast, hue, saturation, lightness) and effects (blur, vignette, noise); reset returns all of them to their defaults. Use ${TOOL_NAMES.setGlobalColorGrading} to grade every scene at once. The asset's current adjustments are its effects entry in the video state (absent means all defaults), with its filter override at effects.colorGrading (absent means it follows the global filter).`;

    private injectedFilters?: ColorGradingFilter[];

    public constructor(args: SetMediaEffectsConstructorArgs) {
        super(args);
        this.injectedFilters = args.colorGradingFilters;
    }

    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const { enum: filterNames, description } = colorGradingFilterParameter(
            activeColorGradingFilters(this.injectedFilters),
        );
        // Two values of our own ride along with the filter names: show no filter, or follow the
        // video's global filter again.
        Object.assign(parameters.properties.color_filter, {
            ...(filterNames
                ? { enum: [...filterNames, COLOR_FILTER_NONE, COLOR_FILTER_GLOBAL] }
                : {}),
            description: `${description}\n"${COLOR_FILTER_NONE}" shows no filter on this asset; "${COLOR_FILTER_GLOBAL}" makes it follow the video's global filter again.`,
        });
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Adjusting media look';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(args: SetMediaEffectsArgs, context: ToolExecutionContext) {
        const { asset_name, color_filter, color_filter_intensity, reset } = args;
        const { parentActionGroup } = context;

        if (ADJUSTMENT_PARAMETERS.every((parameter) => args[parameter] === undefined)) {
            return `Error: Pass at least one adjustment (${ADJUSTMENT_PARAMETERS.join(', ')}).`;
        }

        const changes: string[] = [];
        const notes: string[] = [];
        try {
            const { sceneIndex, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[sceneIndex];
            if (!scene) {
                return `Error: No scene found for scene_id ${sceneShortId}`;
            }
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a media asset`;
            }
            if (!asset.isEditable || isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            // A talking-head presenter slot is adjusted through the scene group's recording, the
            // same target the operations resolve to.
            const { mediaAsset: target } = getAdvancedMediaControlsAsset(
                this.luminary,
                scene.id,
                asset.id,
            );
            if (!target) {
                return `Error: ${asset_name} in scene ${sceneShortId} has no media to adjust`;
            }

            // Resolve every requested change before applying any, so an invalid request can't
            // leave the asset half-updated.
            const look = this.resolveLook(asset, target, args);
            // A reset also drops the asset's own filter, unless the same call picks a new one.
            const colorFilterImpliedByReset = reset === true && color_filter === undefined;
            if (colorFilterImpliedByReset && color_filter_intensity !== undefined) {
                return 'Error: color_filter_intensity needs a color_filter when resetting, since the reset drops the filter it would re-tune.';
            }
            const colorFilterChange = await this.resolveColorFilter(
                target,
                colorFilterImpliedByReset ? COLOR_FILTER_GLOBAL : color_filter,
                color_filter_intensity,
            );
            if (colorFilterChange && 'error' in colorFilterChange) {
                return colorFilterChange.error;
            }

            if (Object.keys(look.values).length > 0) {
                const { setSceneMediaEffects } = makeOperationsCallable(
                    { setSceneMediaEffectsOperation },
                    this.luminary,
                    this.dispatch,
                    () => this.getState(),
                );
                setSceneMediaEffects(scene.id, asset.id, look.values, false, parentActionGroup);
            }
            changes.push(...look.changes);
            notes.push(...look.notes);

            if (colorFilterChange && 'unchanged' in colorFilterChange) {
                if (!colorFilterImpliedByReset) {
                    notes.push(`colour filter unchanged (${colorFilterChange.unchanged})`);
                }
            } else if (colorFilterChange) {
                changes.push(
                    this.applyColorFilter(colorFilterChange, asset.id, scene.id, parentActionGroup),
                );
            }

            const noteText = notes.length > 0 ? ` NOTE: ${notes.join('; ')}.` : '';
            if (changes.length === 0) {
                return `No change: nothing to update for ${asset_name} in scene ${sceneShortId}.${noteText}`;
            }
            // The intensity operations only checkpoint after a shared debounce, which can land
            // after this turn or be swallowed by another edit, so record the change now.
            this.checkpointUnderTurn(parentActionGroup);
            return `Success: Updated ${changes.join(', ')} for ${asset_name} in scene ${sceneShortId}.${noteText}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adjusting media look: ${errorMessage}`;
        }
    }

    /**
     * Turn the colour correction / effect values (and reset) into the Luminary properties to write,
     * with a phrase per change and a note per value that is already in place. Reads the asset but
     * never writes it.
     */
    private resolveLook(
        asset: TimeAwareMediaAsset,
        target: TimeAwareMediaAsset,
        args: SetMediaEffectsArgs,
    ): { values: Partial<MediaEffectValues>; changes: string[]; notes: string[] } {
        const current = getMediaEffectValues(target);
        const values: Partial<MediaEffectValues> = {};
        const changes: string[] = [];
        const notes: string[] = [];
        const names = Object.keys(MEDIA_EFFECT_PROPERTIES) as MediaEffectName[];

        if (args.reset) {
            for (const name of names) {
                const property = MEDIA_EFFECT_PROPERTIES[name];
                if (current[property] !== DEFAULT_MEDIA_EFFECTS[property]) {
                    values[property] = DEFAULT_MEDIA_EFFECTS[property];
                }
            }
            if (Object.keys(values).length > 0) {
                changes.push('look reset to defaults');
            } else {
                notes.push('look already at defaults');
            }
        }

        for (const name of names) {
            const requested = args[name];
            if (requested === undefined) {
                continue;
            }
            // The drawer hides the noise slider for stickers, so a sticker never carries grain.
            if (name === 'noise' && VideoData.isStickerAsset(asset)) {
                notes.push('noise was ignored because stickers do not support it');
                continue;
            }
            const property = MEDIA_EFFECT_PROPERTIES[name];
            if (Math.abs(current[property] - requested) < 0.005) {
                // Still write it under a reset, or the reset would wipe the value the call asked for.
                if (args.reset) {
                    values[property] = requested;
                } else {
                    notes.push(`${name} already ${requested}`);
                }
                continue;
            }
            values[property] = requested;
            changes.push(`${name} to ${requested}`);
        }

        return { values, changes, notes };
    }

    /**
     * Turn color_filter / color_filter_intensity into the change to apply, a reason nothing needs
     * to change, or a complete error message. Reads the asset but never writes it.
     */
    private async resolveColorFilter(
        target: TimeAwareMediaAsset,
        colorFilter: string | undefined,
        intensity: number | undefined,
    ): Promise<ColorFilterChange | { unchanged: string } | { error: string } | undefined> {
        if (colorFilter === undefined && intensity === undefined) {
            return undefined;
        }
        const choice = colorFilter?.toLowerCase();
        const hasOverride = target.gradingOverride !== null || target.enableGreyscale;
        const isUngraded = !target.enableGreyscale && target.gradingOverride?.source === '';

        if (choice === COLOR_FILTER_GLOBAL || choice === COLOR_FILTER_NONE) {
            if (intensity !== undefined) {
                return {
                    error: `Error: color_filter_intensity only applies to a named filter, not to "${colorFilter}".`,
                };
            }
            if (choice === COLOR_FILTER_GLOBAL) {
                return hasOverride
                    ? { kind: 'follow_global' }
                    : { unchanged: "already follows the video's global colour grading" };
            }
            return isUngraded
                ? { unchanged: 'already shows no colour grading filter' }
                : { kind: 'ungraded' };
        }

        if (colorFilter === undefined) {
            return this.resolveIntensityOnly(target, intensity as number);
        }

        const activeFilters = await loadActiveColorGradingFilters(this.dispatch, () =>
            this.getState(),
        );
        const filter = findColorGradingFilterByName(activeFilters, colorFilter);
        if (!filter) {
            return { error: unknownColorGradingFilterError(colorFilter, activeFilters) };
        }
        const isGreyscale = filter.texture_url === MONO_FILTER_TEXTURE_URL;
        const targetIntensity = intensity ?? 1;
        const alreadyApplied = isGreyscale
            ? target.enableGreyscale
            : !target.enableGreyscale &&
              target.gradingOverride?.source === filter.texture_url &&
              Math.abs(target.gradingOverride.intensity - targetIntensity) < 0.005;
        if (alreadyApplied) {
            return {
                unchanged: `already shows the "${filter.name}" filter${isGreyscale ? '' : ` at ${formatPercent(targetIntensity)}`}`,
            };
        }
        return { kind: 'filter', filter, intensity: targetIntensity, isGreyscale };
    }

    private resolveIntensityOnly(
        target: TimeAwareMediaAsset,
        intensity: number,
    ): ColorFilterChange | { unchanged: string } | { error: string } {
        if (target.enableGreyscale) {
            return {
                error: 'Error: the asset shows the greyscale filter, which has no intensity. Pass color_filter to switch to a graded filter.',
            };
        }
        const override = target.gradingOverride;
        if (override && override.source === '') {
            return {
                error: 'Error: the asset shows no filter, so there is no intensity to change. Pass color_filter to apply one.',
            };
        }
        if (!override && !this.luminary.template.grading?.source) {
            return {
                error: 'Error: the asset has no filter to re-tune: the video has no global colour grading. Pass color_filter to apply one.',
            };
        }
        const filterName = getMediaColorGradingOverride(target, this.getState())?.filter;
        if (override && Math.abs(override.intensity - intensity) < 0.005) {
            return {
                unchanged: `already shows the "${filterName}" filter at ${formatPercent(intensity)}`,
            };
        }
        return { kind: 'intensity', intensity, filterName: filterName ?? 'current' };
    }

    /** Apply a resolved colour filter change and describe it for the result summary. */
    private applyColorFilter(
        change: ColorFilterChange,
        assetId: string,
        sceneId: string,
        parentActionGroup: string | undefined,
    ): string {
        const {
            setColorGradingFilterOverride,
            setColorGradingFilterOverrideIntensity,
            removeColorGradingFilterOverride,
        } = makeOperationsCallable(
            {
                setColorGradingFilterOverrideOperation,
                setColorGradingFilterOverrideIntensityOperation,
                removeColorGradingFilterOverrideOperation,
            },
            this.luminary,
            this.dispatch,
            () => this.getState(),
        );

        switch (change.kind) {
            case 'follow_global':
                removeColorGradingFilterOverride(assetId, sceneId, false, parentActionGroup);
                return "colour filter to follow the video's global colour grading";
            case 'ungraded':
                setColorGradingFilterOverride(null, assetId, sceneId, false, parentActionGroup);
                return `colour filter to "${COLOR_FILTER_NONE}" (stays ungraded even if the video has a global filter)`;
            case 'intensity':
                setColorGradingFilterOverrideIntensity(
                    change.intensity,
                    assetId,
                    sceneId,
                    false,
                    parentActionGroup,
                );
                return `"${change.filterName}" colour filter intensity to ${formatPercent(change.intensity)}`;
            case 'filter':
                setColorGradingFilterOverride(
                    change.filter.texture_url,
                    assetId,
                    sceneId,
                    false,
                    parentActionGroup,
                );
                if (change.isGreyscale) {
                    // Greyscale is a switch rather than a graded filter, so intensity does not apply.
                    return `colour filter to "${change.filter.name}" (greyscale)`;
                }
                setColorGradingFilterOverrideIntensity(
                    change.intensity,
                    assetId,
                    sceneId,
                    false,
                    parentActionGroup,
                );
                return `colour filter to "${change.filter.name}" at ${formatPercent(change.intensity)}`;
        }
    }
}
